import type { Language } from '../types';
import { isUiLanguage } from './i18n';

/** Read production tenant host suffix (mirrors server TENANT_HOST_SUFFIX). */
export function getTenantHostSuffix(): string | null {
  if (typeof document !== 'undefined') {
    const fromMeta = document
      .querySelector('meta[name="tenant-host-suffix"]')
      ?.getAttribute('content')
      ?.trim()
      .toLowerCase();
    if (fromMeta) {
      return fromMeta;
    }
  }

  return null;
}

function slugFromHostSuffix(host: string, suffix: string): string | null {
  if (!host.endsWith(`.${suffix}`)) {
    return null;
  }
  const slug = host.slice(0, -(suffix.length + 1));
  if (!slug || slug === 'www') {
    return null;
  }
  return slug;
}

function guessSlugFromMultiPartHost(host: string): string | null {
  const parts = host.toLowerCase().split('.').filter(Boolean);
  // electra.catalogofworks.com → electra (fallback when meta / TENANT_HOST_SUFFIX missing)
  if (parts.length >= 3 && parts[0] !== 'www') {
    return parts[0];
  }
  return null;
}

/** Tenant slug from subdomain, e.g. demo.localhost or electra.catalogofworks.com → slug */
export function tenantSlugFromHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  hostSuffix = getTenantHostSuffix(),
): string | null {
  const host = hostname.toLowerCase();

  if (host.endsWith('.localhost')) {
    const slug = host.slice(0, -'.localhost'.length);
    return slug && slug !== 'localhost' ? slug : null;
  }

  if (hostSuffix) {
    const fromSuffix = slugFromHostSuffix(host, hostSuffix.toLowerCase());
    if (fromSuffix) {
      return fromSuffix;
    }
  }

  return guessSlugFromMultiPartHost(host);
}

/** True when tenant is resolved from subdomain (prod or demo.localhost). */
export function isTenantSubdomainHost(): boolean {
  return Boolean(tenantSlugFromHostname());
}

/** Apex host for UI copy (e.g. catalogofworks.com) — not a tenant subdomain. */
export function platformApexDisplayHost(): string {
  if (typeof window === 'undefined') return 'catalogofworks.com';
  const suffix = getTenantHostSuffix();
  if (suffix) return suffix;
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) return 'localhost';
  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 2) return parts.slice(-2).join('.');
  return host || 'catalogofworks.com';
}

/** Service landing on the apex host; empty when the apex cannot be told from this host. */
export function platformLandingUrl(): string {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname, port } = window.location;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return `${protocol}//localhost${port ? `:${port}` : ''}/`;
  }
  const suffix = getTenantHostSuffix();
  if (suffix) return `${protocol}//${suffix}/`;
  const parts = host.split('.').filter(Boolean);
  return parts.length >= 2 ? `${protocol}//${parts.slice(-2).join('.')}/` : '';
}

function tenantSubdomainOrigin(slug: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const suffix = getTenantHostSuffix();
  if (suffix) {
    return `${window.location.protocol}//${slug}.${suffix}`;
  }
  const host = window.location.hostname.toLowerCase();
  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 2) {
    const apex = parts.slice(-2).join('.');
    return `${window.location.protocol}//${slug}.${apex}`;
  }
  return null;
}

/** Append `lang` for platform → storefront handoff (separate subdomain localStorage). */
export function withPreferredLang(url: string, lang?: Language | null): string {
  if (!isUiLanguage(lang)) return url;
  try {
    const absolute = /^https?:\/\//i.test(url);
    const parsed = absolute ? new URL(url) : new URL(url, window.location.origin);
    parsed.searchParams.set('lang', lang);
    if (absolute) return parsed.toString();
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    const join = url.includes('?') ? '&' : '?';
    return `${url}${join}lang=${encodeURIComponent(lang)}`;
  }
}

/** Public catalog URL — subdomain on prod, ?tenant= on bare localhost. */
export function tenantCatalogUrl(slug: string, lang?: Language | null): string {
  const origin = tenantSubdomainOrigin(slug);
  const base = origin
    ? `${origin}/app/catalog`
    : `/app/catalog?tenant=${encodeURIComponent(slug)}`;
  return withPreferredLang(base, lang);
}

/** Catalog deep link that opens a product card on arrival. */
export function tenantCatalogProductUrl(
  slug: string,
  productId: string,
  lang?: Language | null,
): string {
  const origin = tenantSubdomainOrigin(slug);
  const productParam = `product=${encodeURIComponent(productId)}`;
  let base: string;
  if (origin) {
    base = `${origin}/app/catalog?${productParam}`;
  } else {
    const catalogPath = appPathWithTenant('/app/catalog', slug);
    const [pathname, search = ''] = catalogPath.split('?');
    const params = new URLSearchParams(search);
    params.set('product', productId);
    const qs = params.toString();
    base = qs ? `${pathname}?${qs}` : pathname;
  }
  return withPreferredLang(base, lang);
}

/** Photographer cabinet on the tenant host. Null on bare localhost, where ?tenant= stays. */
export function tenantCabinetUrl(slug: string, path = '/app'): string | null {
  const origin = tenantSubdomainOrigin(slug);
  if (!origin) return null;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalized}`;
}

export function tenantDevUrl(slug: string, lang?: Language | null): string {
  const origin = tenantSubdomainOrigin(slug);
  const base = origin ? `${origin}/` : `/?tenant=${encodeURIComponent(slug)}`;
  return withPreferredLang(base, lang);
}

export function tenantLoginUrl(slug: string, lang?: Language | null): string {
  const origin = tenantSubdomainOrigin(slug);
  const base = origin ? `${origin}/login` : `/login?tenant=${encodeURIComponent(slug)}`;
  return withPreferredLang(base, lang);
}

/** Platform console → tenant admin via one-time enter code (consume on /login). */
export function tenantPlatformEnterUrl(
  slug: string,
  enterToken: string,
  lang?: Language | null,
): string {
  const origin = tenantSubdomainOrigin(slug);
  const params = new URLSearchParams();
  params.set('platformEnter', enterToken);
  if (!origin) {
    params.set('tenant', slug);
  }
  const qs = params.toString();
  const base = origin ? `${origin}/login?${qs}` : `/login?${qs}`;
  return withPreferredLang(base, lang);
}

/** Public digital album URL — always on the tenant host (subdomain or ?tenant=). */
export function tenantAlbumPublicUrl(slug: string, publicCode: string): string {
  const code = encodeURIComponent(String(publicCode || '').trim());
  const origin = tenantSubdomainOrigin(slug);
  if (origin) {
    return `${origin}/a/${code}`;
  }
  return `/a/${code}?tenant=${encodeURIComponent(slug)}`;
}

/** Public digital visiting card URL — always on the tenant host. */
export function tenantVisitingCardPublicUrl(slug: string): string {
  const origin = tenantSubdomainOrigin(slug);
  if (origin) {
    return `${origin}/c`;
  }
  return `/c?tenant=${encodeURIComponent(slug)}`;
}

/** Map nav item paths to include ?tenant= on bare localhost. */
export function mapNavItemPaths<T extends { path: string }>(
  items: T[],
  tenantSlug?: string | null,
): T[] {
  if (!tenantSlug || isTenantSubdomainHost()) {
    return items;
  }
  return items.map((item) => ({ ...item, path: appPathWithTenant(item.path, tenantSlug) }));
}

/** Slug for tenant-scoped API calls (subdomain, ?tenant=, or explicit). */
export function resolveTenantSlugForApi(explicit?: string | null): string | null {
  if (explicit?.trim()) {
    return explicit.trim().toLowerCase();
  }
  const fromHost = tenantSlugFromHostname();
  if (fromHost) {
    return fromHost;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  const fromQuery = new URLSearchParams(window.location.search).get('tenant')?.trim().toLowerCase();
  return fromQuery || null;
}

/** Headers so /api/tenant/* resolves the warehouse when Host is apex or behind proxy. */
export function tenantScopeHeaders(explicitSlug?: string | null): Record<string, string> {
  const slug = resolveTenantSlugForApi(explicitSlug);
  return slug ? { 'X-Tenant-Slug': slug } : {};
}

/** Append ?tenant= when dev uses bare localhost (no subdomain). */
export function appPathWithTenant(path: string, tenantSlug?: string | null): string {
  if (!tenantSlug || isTenantSubdomainHost()) {
    return path;
  }
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  if (!params.has('tenant')) {
    params.set('tenant', tenantSlug);
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
